import React, { useState } from 'react';
import * as LucideIcons from 'lucide-react';
import { X, CheckCircle, XCircle, ChevronDown, ChevronRight, Save } from 'lucide-react';
import clsx from 'clsx';
import { apiService } from '../../../../components/api/apiService';

export interface RoutePrivilege {
    id: string;
    uid: string;
    label: string;
    path: string | null;
    icon: string;
    hasAccess: boolean;
    children?: RoutePrivilege[];
}

interface Props {

    roleName: string;
    roleId: string;
    tenantId: string;
    routes: RoutePrivilege[];
    onClose: () => void;
}

const Toast: React.FC<{ message: string }> = ({ message }) => (
    <div className="absolute top-4 right-6 z-50 bg-green-600 text-white text-sm px-4 py-2 rounded shadow-lg animate-fadeIn">
        ✅ {message}
    </div>
);

const ViewRoutePrivilegesModal: React.FC<Props> = ({
    roleName,
    roleId,
    tenantId,
    routes,
    onClose
}) => {
    const [editedRoutes, setEditedRoutes] = useState<RoutePrivilege[]>(routes);
    const [selectedRouteId, setSelectedRouteId] = useState<string | null>(null);
    const [toastMessage, setToastMessage] = useState<string | null>(null);
    const [saving, setSaving] = useState<boolean>(false);

    const toggleAccess = (target: RoutePrivilege, nodes = editedRoutes): RoutePrivilege[] => {
        return nodes.map((r) => {
            if (r.uid === target.uid) return { ...r, hasAccess: !r.hasAccess };
            if (r.children) return { ...r, children: toggleAccess(target, r.children) };
            return r;
        });
    };

    const handleToggle = (route: RoutePrivilege) => {
        const updated = toggleAccess(route);
        setEditedRoutes(updated);
    };

    const flattenRouteTree = (nodes: RoutePrivilege[]): RoutePrivilege[] => {
        return nodes.flatMap((node) => {
            const { children, ...rest } = node;
            const flattened = children ? flattenRouteTree(children) : [];
            return [{ ...rest }, ...flattened];
        });
    };


    const handleSave = async () => {
        setSaving(true);
        try {
            const payload = {
                tenantId,
                roleId,
                routes: editedRoutes
            };
            await apiService.post('/api/RoutePermission/upsert', payload, 'backend');
            setToastMessage('Permissions saved successfully!');
        } catch (error) {
            console.error('Save failed:', error);
            setToastMessage('❌ Failed to save permissions');
        } finally {
            setSaving(false);
            setTimeout(() => setToastMessage(null), 2000);
        }
    };

    const selectedRoute = findRoute(editedRoutes, selectedRouteId);

    return (
        <div className="fixed inset-0 bg-black bg-opacity-70 z-50 flex items-center justify-center p-6">
            <div className="bg-neutral-900 text-white rounded-xl w-full max-w-5xl p-6 shadow-2xl relative max-h-[90vh] overflow-hidden">
                <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-white">
                    <X className="w-5 h-5" />
                </button>

                {toastMessage && <Toast message={toastMessage} />}

                <div className="flex justify-between items-center mb-4 gap-6">
                    <h2 className="text-xl font-semibold">
                        Route Privileges for <span className="text-indigo-400">{roleName}</span>
                    </h2>
                    <button
                        onClick={handleSave}
                        disabled={saving}
                        className="flex items-center gap-2 text-sm px-4 py-2 rounded bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
                    >
                        <Save className="w-4 h-4" />
                        {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                </div>

                <div className="flex border-t border-neutral-700 pt-4 gap-4 overflow-hidden">
                    {/* Left Tree */}
                    <div className="w-2/3 overflow-y-auto pr-2 border-r border-neutral-800 max-h-[70vh]">
                        <RouteTree
                            routes={editedRoutes}
                            onToggle={handleToggle}
                            selected={selectedRouteId}
                            onSelect={setSelectedRouteId}
                        />
                    </div>

                    {/* Right Panel */}
                    <div className="w-1/3 text-sm text-gray-300 pl-4">
                        {selectedRoute ? (
                            <div>
                                <p className="font-semibold text-white mb-2">Selected Route</p>
                                <p><span className="text-indigo-400">UID:</span> {selectedRoute.uid}</p>
                                <p><span className="text-indigo-400">Label:</span> {selectedRoute.label}</p>
                                <p><span className="text-indigo-400">Path:</span> {selectedRoute.path || '—'}</p>
                                <p><span className="text-indigo-400">Access:</span> {selectedRoute.hasAccess ? 'Granted ✅' : 'Denied ❌'}</p>
                            </div>
                        ) : (
                            <p className="text-gray-500">Click a route to view details</p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

const RouteTree: React.FC<{
    routes: RoutePrivilege[];
    onToggle: (route: RoutePrivilege) => void;
    selected: string | null;
    onSelect: (uid: string) => void;
}> = ({ routes, onToggle, selected, onSelect }) => {
    const [expanded, setExpanded] = useState<{ [uid: string]: boolean }>({});

    return (
        <ul className="ml-1 space-y-1">
            {routes.map((route) => {
                const isExpanded = expanded[route.uid];
                const getLucideIcon = (iconName: string) => {
                    const IconComponent = (LucideIcons as any)[iconName];
                    return IconComponent || LucideIcons.SquareDot;
                };
                const Icon = getLucideIcon(route.icon);

                return (
                    <li key={route.uid}>
                        <div
                            className={clsx(
                                'flex items-center justify-between rounded px-3 py-2',
                                'hover:bg-neutral-800 cursor-pointer group',
                                selected === route.uid ? 'bg-neutral-800 border border-indigo-500' : ''
                            )}
                            onClick={() => onSelect(route.uid)}
                        >
                            <div className="flex items-center gap-2">
                                {route.children?.length ? (
                                    <button
                                        className="text-gray-400 group-hover:text-white"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setExpanded((prev) => ({ ...prev, [route.uid]: !isExpanded }));
                                        }}
                                    >
                                        {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                                    </button>
                                ) : (
                                    <span className="w-4 h-4" />
                                )}
                                <Icon className="w-4 h-4 text-yellow-400" />
                                <span className="text-gray-200 text-sm">{route.label}</span>
                            </div>
                            <button
                                className="text-xs bg-neutral-700 px-2 py-1 rounded hover:bg-indigo-600"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onToggle(route);
                                }}
                            >
                                {route.hasAccess ? (
                                    <CheckCircle className="text-green-400 w-4 h-4" />
                                ) : (
                                    <XCircle className="text-red-400 w-4 h-4" />
                                )}
                            </button>
                        </div>
                        {route.children && isExpanded && (
                            <div className="ml-4">
                                <RouteTree
                                    routes={route.children}
                                    onToggle={onToggle}
                                    selected={selected}
                                    onSelect={onSelect}
                                />
                            </div>
                        )}
                    </li>
                );
            })}
        </ul>
    );
};

function findRoute(routes: RoutePrivilege[], uid: string | null): RoutePrivilege | undefined {
    if (!uid) return undefined;
    for (const r of routes) {
        if (r.uid === uid) return r;
        if (r.children) {
            const found = findRoute(r.children, uid);
            if (found) return found;
        }
    }
    return undefined;
}

export default ViewRoutePrivilegesModal;
